// parts/TimetableSync.tsx
// One place for everything about "Sync": the API calls, the full-screen
// progress overlay, the confirmation, and the guided problem report.
// Used by the Timetable page header AND the Manage Timetable Types modal.
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/services/axiosInterceptor';

/* ══════════════════════════════════════════════════════════════════════
   Days helpers (also used by the Manage Timetable Entry modal)
══════════════════════════════════════════════════════════════════════ */
const SHORT_DAYS: Record<string, string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
};

export const shortDay = (day: string): string => SHORT_DAYS[day] ?? day;

const toDayList = (days: unknown): string[] =>
  Array.isArray(days) ? (days as string[]) : typeof days === 'string' && days ? [days] : [];

/** "Mon, Tue, Wed" — or "All days (Mon–Sat)" when every working day is used. */
export const formatDays = (days: unknown): string => {
  const list = toDayList(days);
  if (list.length === 0) return '';
  if (list.length >= 6) return 'All days (Mon–Sat)';
  return list.map(shortDay).join(', ');
};

/* ══════════════════════════════════════════════════════════════════════
   Types
══════════════════════════════════════════════════════════════════════ */
export interface Anomaly {
  type: string;
  message: string;
  entry_ids?: string[];
  class_name?: string;
  section?: string;
  subject_name?: string;
  teachers?: string[];
  period_label?: string;
  day?: string;
}

export interface SyncReport {
  typeName: string;
  /** 'blocked' = sync refused, 'warnings' = synced with notes, 'check' = dry run */
  mode: 'blocked' | 'warnings' | 'check';
  anomalies: Anomaly[];
  warnings: Anomaly[];
  assignmentsToBuild?: number;
}

/** What the page already knows about an entry id — lets the report say exactly where a problem is. */
export interface EntryInfo {
  class_name?: string;
  section?: string | null;
  subject_name?: string;
  teacher_name?: string;
  period_label?: string;
  day_of_week?: unknown;
}
export type EntryLookup = (entryId: string) => EntryInfo | undefined;

/* ══════════════════════════════════════════════════════════════════════
   Plain-language guidance for every problem the backend can report
══════════════════════════════════════════════════════════════════════ */
interface Guide {
  title: string;
  why: string;
  fix: string[];
}

const GUIDES: Record<string, Guide> = {
  empty_timetable: {
    title: 'This timetable has no entries',
    why: 'Syncing an empty timetable would remove every teacher assignment for the session, so it is blocked.',
    fix: [
      'Add entries with Add Entry, or copy another timetable with Clone.',
      'Click Sync again.',
    ],
  },
  multiple_teachers: {
    title: 'A subject has more than one teacher in the same class',
    why: 'A subject can have only one teacher per class and section. These entries use two or more teachers for the same subject.',
    fix: [
      'Decide which teacher should teach the subject for this class and section.',
      'Click the period in the timetable and delete the entries that use the other teacher.',
      'Add those periods again with the right teacher, then click Sync again.',
    ],
  },
  missing_subject_or_teacher: {
    title: 'A teaching period has no subject or no teacher',
    why: 'Every teaching period needs both, so the teacher can be given that class and subject.',
    fix: [
      'Find the period in the timetable for the class listed below.',
      'Delete the incomplete entry.',
      'Add it again with a subject and a teacher, then click Sync again.',
    ],
  },
  missing_teacher: {
    title: 'A teacher in the timetable no longer exists',
    why: 'The teacher was removed from the school, but the timetable still points to them.',
    fix: [
      'Click the period, delete the entry, and add it again with a current teacher.',
      'Click Sync again.',
    ],
  },
  missing_subject: {
    title: 'A subject in the timetable no longer exists',
    why: 'The subject was deleted from the class, but the timetable still points to it.',
    fix: [
      'Add the subject back to the class, or delete the entry and add it again with another subject.',
      'Click Sync again.',
    ],
  },
  missing_class: {
    title: 'A class in the timetable no longer exists',
    why: 'The class was removed from this session, but timetable entries still point to it. These entries may not be visible in the grid.',
    fix: [
      'Restore the class for this session, or remove the entries that belong to it.',
      'If you cannot find the entries, contact support and share how many are listed below.',
    ],
  },
  invalid_section: {
    title: 'A section in the timetable no longer exists on its class',
    why: 'The section was removed or renamed after these entries were created.',
    fix: [
      'Add the section back to the class, or delete the entries and add them again with an existing section.',
      'Click Sync again.',
    ],
  },
  missing_time_slot: {
    title: 'A period in the timetable no longer exists',
    why: 'The period was deleted from Time Slots after these entries were created.',
    fix: [
      'Add the period back under Time Slots with the same number and label, or delete these entries.',
      'Click Sync again.',
    ],
  },
  teacher_clash: {
    title: 'A teacher is booked in two classes at the same time',
    why: 'This does not stop syncing, but one teacher cannot be in two places at once.',
    fix: [
      'Move one of the periods to a different time slot, or give it to another teacher.',
    ],
  },
};

const fallbackGuide = (a: Anomaly): Guide => ({
  title: a.type.replace(/_/g, ' '),
  why: a.message,
  fix: [],
});

const groupByType = (list: Anomaly[]): Array<[string, Anomaly[]]> => {
  const map = new Map<string, Anomaly[]>();
  list.forEach((a) => map.set(a.type, [...(map.get(a.type) ?? []), a]));
  return Array.from(map.entries());
};

/** Heading for one problem: where it is, in plain words. */
const whereHeading = (a: Anomaly): string =>
  [a.class_name, a.section ? `Section ${a.section}` : null, a.subject_name].filter(Boolean).join(' • ');

/** Detail lines under a problem. Uses the page's own data when it has it. */
const detailLines = (a: Anomaly, lookup?: EntryLookup): string[] => {
  const infos = (a.entry_ids ?? [])
    .map((id) => lookup?.(id))
    .filter((i): i is EntryInfo => !!i);

  if (a.type === 'multiple_teachers' && infos.length > 0) {
    const byTeacher = new Map<string, string[]>();
    infos.forEach((i) => {
      const teacher = i.teacher_name || 'Unknown teacher';
      const days = formatDays(i.day_of_week);
      byTeacher.set(teacher, [...(byTeacher.get(teacher) ?? []), `${i.period_label || 'Period'}${days ? ` (${days})` : ''}`]);
    });
    return Array.from(byTeacher.entries()).map(([teacher, periods]) => `${teacher}: ${periods.join('; ')}`);
  }

  if (infos.length > 0) {
    const lines = infos.slice(0, 5).map((i) =>
      [
        i.class_name,
        i.section ? `Section ${i.section}` : null,
        i.subject_name,
        i.teacher_name,
        i.period_label,
        formatDays(i.day_of_week),
      ]
        .filter(Boolean)
        .join(' • '),
    );
    if (infos.length > 5) lines.push(`and ${infos.length - 5} more`);
    return lines;
  }

  // No page data for these entries — use what the backend sent.
  const fallback = [
    a.teachers && a.teachers.length > 0 ? a.teachers.join(' and ') : null,
    a.period_label,
    a.day,
  ]
    .filter(Boolean)
    .join(' • ');
  const count = a.entry_ids?.length ?? 0;
  return [fallback, count > 1 ? `${count} entries affected` : ''].filter(Boolean);
};

/* ══════════════════════════════════════════════════════════════════════
   Hook
══════════════════════════════════════════════════════════════════════ */
export function useTimetableSync(
  toast: React.RefObject<Toast | null>,
  onSynced?: () => void | Promise<void>,
) {
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [report, setReport] = useState<SyncReport | null>(null);

  const busy = useRef(false);
  const onSyncedRef = useRef(onSynced);
  onSyncedRef.current = onSynced;

  const sync = useCallback(
    async (typeId: string, typeName: string, wasActive = false) => {
      if (!typeId || busy.current) return;
      busy.current = true;
      setSyncingId(typeId);
      try {
        // The backend checks everything first. If anything is wrong it answers 409
        // with the list and changes nothing; otherwise it activates the type and
        // rebuilds the teacher assignments for the session from its entries.
        const res = await apiClient.post(`/timetable-types/${typeId}/set-active`);
        if (res.data.status === 'success') {
          const warnings: Anomaly[] = res.data.warnings ?? [];
          const built = res.data.assignments_created ?? 0;
          toast.current?.show({
            severity: warnings.length > 0 ? 'warn' : 'success',
            summary: wasActive ? 'Synced' : 'Activated and synced',
            detail: `"${res.data.name ?? typeName}" — ${built} teacher assignment${built === 1 ? '' : 's'} built from the timetable.`,
            life: 4500,
          });
          if (warnings.length > 0) {
            setReport({ typeName, mode: 'warnings', anomalies: [], warnings });
          }
          await onSyncedRef.current?.();
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Sync failed',
            detail: res.data.message || 'Could not sync this timetable.',
            life: 5000,
          });
        }
      } catch (err: any) {
        const data = err.response?.data;
        if (err.response?.status === 409 && Array.isArray(data?.anomalies) && data.anomalies.length > 0) {
          setReport({ typeName, mode: 'blocked', anomalies: data.anomalies, warnings: data.warnings ?? [] });
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Sync failed',
            detail: data?.message || 'Could not sync this timetable.',
            life: 5000,
          });
        }
      } finally {
        busy.current = false;
        setSyncingId(null);
      }
    },
    [toast],
  );

  /** Dry run — reports problems without changing anything. */
  const check = useCallback(
    async (typeId: string, typeName: string) => {
      if (!typeId) return;
      setCheckingId(typeId);
      try {
        const res = await apiClient.get(`/timetable-types/${typeId}/validate`);
        if (res.data.status === 'success') {
          setReport({
            typeName,
            mode: 'check',
            anomalies: res.data.errors ?? [],
            warnings: res.data.warnings ?? [],
            assignmentsToBuild: res.data.assignments_to_build,
          });
        } else {
          toast.current?.show({ severity: 'error', summary: 'Check failed', detail: res.data.message || 'Could not check this timetable.', life: 5000 });
        }
      } catch (err: any) {
        toast.current?.show({
          severity: 'error',
          summary: 'Check failed',
          detail: err.response?.data?.message || 'Could not check this timetable.',
          life: 5000,
        });
      } finally {
        setCheckingId(null);
      }
    },
    [toast],
  );

  return {
    syncing: syncingId !== null,
    syncingId,
    checkingId,
    report,
    closeReport: () => setReport(null),
    sync,
    check,
  };
}

/* ══════════════════════════════════════════════════════════════════════
   Full-screen overlay: the timetable stays visible behind, faded.
══════════════════════════════════════════════════════════════════════ */
export const SyncOverlay: React.FC<{ visible: boolean }> = ({ visible }) => {
  if (!visible) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000, // above PrimeReact dialogs, so it also covers the types modal
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(248, 250, 252, 0.72)',
        cursor: 'wait',
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '20px',
          padding: '28px 36px',
          border: '1px solid #dbeafe',
          boxShadow: '0 20px 50px rgba(30, 64, 175, 0.18)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          maxWidth: '340px',
          textAlign: 'center',
        }}
      >
        <ProgressSpinner style={{ width: '52px', height: '52px' }} strokeWidth="4" />
        <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e3a8a' }}>Syncing teacher assignments</div>
        <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>
          Checking the timetable and rebuilding assignments. Please keep this page open.
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   Hint: a hand pointing at the Sync button
   Shown after the timetable is edited, because teacher assignments only
   catch up when Sync is clicked. The page header owns the button, so we
   find it in the DOM and follow it as the page scrolls or resizes.
══════════════════════════════════════════════════════════════════════ */
interface HintPosition {
  left: number;
  top: number;
  offscreen: boolean;
}

const findSyncButton = (): HTMLElement | null =>
  (Array.from(document.querySelectorAll('button')).find(
    (b) =>
      !b.closest('.p-dialog') && // ignore the buttons inside dialogs
      (b.textContent?.trim() === 'Sync' || !!b.querySelector('.pi-sync')),
  ) as HTMLElement | undefined) ?? null;

export const SyncHint: React.FC<{ visible: boolean; onDismiss: () => void }> = ({ visible, onDismiss }) => {
  const [pos, setPos] = useState<HintPosition | null>(null);

  useEffect(() => {
    if (!visible) {
      setPos(null);
      return;
    }

    const measure = () => {
      const btn = findSyncButton();
      if (!btn) {
        setPos(null);
        return;
      }
      const r = btn.getBoundingClientRect();
      const offscreen = r.bottom < 0 || r.top > window.innerHeight;
      const next: HintPosition = {
        // keep the label inside the screen on narrow widths
        left: Math.min(Math.max(r.left + r.width / 2, 110), window.innerWidth - 110),
        top: offscreen ? 8 : r.bottom + 4,
        offscreen,
      };
      setPos((prev) =>
        prev && prev.left === next.left && prev.top === next.top && prev.offscreen === next.offscreen ? prev : next,
      );
    };

    measure();
    const timer = window.setInterval(measure, 400); // catches layout shifts the events miss
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [visible]);

  if (!visible || !pos) return null;

  return (
    <div
      style={{
        position: 'fixed',
        left: pos.left,
        top: pos.top,
        transform: 'translateX(-50%)',
        zIndex: 1000, // above the page, below dialogs and the sync overlay
        pointerEvents: 'none', // never blocks clicks on what is underneath
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <style>{`
        @keyframes tsHintPoint {
          0%, 100% { transform: translateY(8px); }
          50% { transform: translateY(-4px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .ts-hint-hand { animation: none !important; }
        }
      `}</style>

      {/* index finger up, tapping toward the button */}
      <div
        className="ts-hint-hand"
        aria-hidden="true"
        style={{
          fontSize: '38px',
          lineHeight: 1,
          animation: 'tsHintPoint 0.9s ease-in-out infinite',
          filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.25))',
        }}
      >
        👆
      </div>

      <div
        role="status"
        style={{
          pointerEvents: 'auto',
          marginTop: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          whiteSpace: 'nowrap',
          background: '#1e3a8a',
          color: 'white',
          fontSize: '12.5px',
          fontWeight: 600,
          padding: '6px 8px 6px 12px',
          borderRadius: '10px',
          boxShadow: '0 6px 18px rgba(30, 58, 138, 0.3)',
        }}
      >
        <span>{pos.offscreen ? 'Scroll up and click Sync' : 'Click Sync to update teacher assignments'}</span>
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            border: 'none',
            background: 'rgba(255, 255, 255, 0.18)',
            color: 'white',
            width: '20px',
            height: '20px',
            borderRadius: '6px',
            cursor: 'pointer',
            lineHeight: 1,
            fontSize: '14px',
            padding: 0,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════
   Confirmation
══════════════════════════════════════════════════════════════════════ */
interface SyncConfirmDialogProps {
  /** Name of the timetable type to sync; null keeps the dialog closed. */
  typeName: string | null;
  /** true = already active, false = will become active, undefined = unknown. */
  isActive?: boolean;
  sessionName?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export const SyncConfirmDialog: React.FC<SyncConfirmDialogProps> = ({
  typeName,
  isActive,
  sessionName,
  onCancel,
  onConfirm,
}) => (
  <Dialog
    visible={!!typeName}
    onHide={onCancel}
    showHeader={false}
    style={{ width: '90vw', maxWidth: '460px', borderRadius: '20px', overflow: 'hidden' }}
    contentStyle={{ padding: '26px', borderRadius: '20px' }}
    modal
    dismissableMask
    draggable={false}
    resizable={false}
  >
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div
          style={{
            width: '46px',
            height: '46px',
            borderRadius: '50%',
            flexShrink: 0,
            background: 'rgba(16,185,129,0.12)',
            color: '#059669',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <i className="pi pi-sync" style={{ fontSize: '19px' }} />
        </div>
        <div style={{ fontSize: '16.5px', fontWeight: 700, color: '#1e293b' }}>
          {isActive === false ? 'Set active and sync?' : 'Sync teacher assignments?'}
        </div>
      </div>
      <div style={{ fontSize: '13.5px', color: '#475569', lineHeight: 1.6 }}>
        <strong>{typeName}</strong> is checked first. If it is clean, every teacher assignment for{' '}
        {sessionName || 'this session'} is replaced with the teachers scheduled in this timetable.
        {isActive === false && ' It also becomes the active timetable.'}
        {isActive === undefined && ' If it is not already the active timetable, it becomes active.'} If anything is
        wrong, nothing changes and you will see how to fix it.
      </div>
      <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
        <button
          onClick={onCancel}
          style={{
            flex: 1,
            padding: '10px',
            borderRadius: '10px',
            border: '1.5px solid #e2e8f0',
            background: 'white',
            color: '#475569',
            fontSize: '13px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          style={{
            flex: 1,
            padding: '10px',
            borderRadius: '10px',
            border: 'none',
            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
            color: 'white',
            fontSize: '13px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {isActive === false ? 'Set active and sync' : 'Sync'}
        </button>
      </div>
    </div>
  </Dialog>
);

/* ══════════════════════════════════════════════════════════════════════
   Problem report with guidance
══════════════════════════════════════════════════════════════════════ */
interface SyncReportDialogProps {
  report: SyncReport | null;
  onClose: () => void;
  lookup?: EntryLookup;
}

const GroupCard: React.FC<{
  type: string;
  items: Anomaly[];
  tone: { color: string; bg: string };
  lookup?: EntryLookup;
}> = ({ type, items, tone, lookup }) => {
  const guide = GUIDES[type] ?? fallbackGuide(items[0]);
  return (
    <div
      style={{
        background: tone.bg,
        border: `1px solid ${tone.color}33`,
        borderRadius: '14px',
        padding: '14px 16px',
        marginBottom: '12px',
      }}
    >
      <div style={{ fontSize: '14px', fontWeight: 700, color: tone.color }}>
        {guide.title} ({items.length})
      </div>
      <div style={{ fontSize: '13px', color: '#475569', lineHeight: 1.55, marginTop: '4px' }}>{guide.why}</div>

      {guide.fix.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <div style={{ fontSize: '12.5px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>How to fix it</div>
          <ol style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#334155', lineHeight: 1.6 }}>
            {guide.fix.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>
      )}

      <div style={{ marginTop: '12px' }}>
        <div style={{ fontSize: '12.5px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>Where</div>
        {items.map((a, i) => {
          const heading = whereHeading(a);
          const lines = detailLines(a, lookup);
          return (
            <div
              key={i}
              style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                padding: '8px 12px',
                marginBottom: '6px',
              }}
            >
              {heading && <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>{heading}</div>}
              {lines.map((line, j) => (
                <div key={j} style={{ fontSize: '12.5px', color: '#475569', lineHeight: 1.5 }}>
                  {line}
                </div>
              ))}
              {!heading && lines.length === 0 && (
                <div style={{ fontSize: '12.5px', color: '#475569' }}>{a.message}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const SyncReportDialog: React.FC<SyncReportDialogProps> = ({ report, onClose, lookup }) => (
  <Dialog
    visible={!!report}
    onHide={onClose}
    showHeader={false}
    style={{ width: '90vw', maxWidth: '640px', borderRadius: '20px', overflow: 'hidden' }}
    contentStyle={{ padding: '24px', borderRadius: '20px', maxHeight: '82vh', overflowY: 'auto' }}
    modal
    dismissableMask
    draggable={false}
    resizable={false}
  >
    {report &&
      (() => {
        const blocked = report.anomalies.length > 0;
        const hasWarnings = report.warnings.length > 0;
        const accent = blocked ? '#dc2626' : hasWarnings ? '#d97706' : '#059669';
        const accentBg = blocked ? 'rgba(239,68,68,0.1)' : hasWarnings ? 'rgba(245,158,11,0.12)' : 'rgba(16,185,129,0.12)';
        const icon = blocked ? 'pi pi-times-circle' : hasWarnings ? 'pi pi-exclamation-triangle' : 'pi pi-check-circle';

        const title = blocked
          ? report.mode === 'blocked'
            ? `"${report.typeName}" could not be synced`
            : `"${report.typeName}" has problems to fix`
          : report.mode === 'warnings'
            ? `"${report.typeName}" was synced, with notes`
            : hasWarnings
              ? `"${report.typeName}" can be synced, with notes`
              : `"${report.typeName}" is ready to sync`;

        const intro = blocked
          ? `Fix ${report.anomalies.length === 1 ? 'this problem' : `these ${report.anomalies.length} problems`}, then click Sync again.${
              report.mode === 'blocked' ? ' Nothing was changed — your current teacher assignments are untouched.' : ''
            }`
          : report.mode === 'check' && typeof report.assignmentsToBuild === 'number'
            ? `${report.assignmentsToBuild} teacher assignment${report.assignmentsToBuild === 1 ? '' : 's'} would be built from this timetable.`
            : '';

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '50%',
                  flexShrink: 0,
                  background: accentBg,
                  color: accent,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className={icon} style={{ fontSize: '20px' }} />
              </div>
              <div>
                <div style={{ fontSize: '16.5px', fontWeight: 700, color: '#1e293b' }}>{title}</div>
                {intro && <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px', lineHeight: 1.5 }}>{intro}</div>}
              </div>
            </div>

            {blocked &&
              groupByType(report.anomalies).map(([type, items]) => (
                <GroupCard key={type} type={type} items={items} tone={{ color: '#dc2626', bg: '#fef2f2' }} lookup={lookup} />
              ))}

            {hasWarnings && (
              <>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#92400e' }}>
                  Notes — these do not stop the sync
                </div>
                {groupByType(report.warnings).map(([type, items]) => (
                  <GroupCard key={type} type={type} items={items} tone={{ color: '#d97706', bg: '#fffbeb' }} lookup={lookup} />
                ))}
              </>
            )}

            <button
              onClick={onClose}
              style={{
                padding: '10px',
                borderRadius: '10px',
                border: 'none',
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                color: 'white',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        );
      })()}
  </Dialog>
);